/** PROTOTYPE variant: Master / Detail (two-pane) -- THROWAWAY. */
import React, { useState } from 'react';
import {
  ProjectState, ModuleState, SourceRow, ModuleKey,
  CRC, DIFFICULTY, DATA_TYPES, CAPTURE_PATH_LABELS,
  estimateModule, estimateProject, money,
} from './moduleInputsData';
import { Label, NumberInput, TextInput, Select, Toggle, Chip, Segmented, Card } from './protoAtoms';

interface Props { project: ProjectState; setProject: React.Dispatch<React.SetStateAction<ProjectState>> }

const th: React.CSSProperties = { fontSize: 10, fontWeight: 700, color: CRC.heritageGrey, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', padding: '8px 10px' };
const td: React.CSSProperties = { fontSize: 12, color: CRC.textGrey, padding: '8px 10px', verticalAlign: 'top', borderTop: `1px solid ${CRC.rowDivider}` };

const EstStat: React.FC<{ e: { oneTime: number; recurring: number; total: number } }> = ({ e }) => (
  <div style={{ display: 'flex', gap: 22 }}>
    <div><Label>One-time</Label><div style={{ fontSize: 14, color: CRC.textGrey }}>{money(e.oneTime)}</div></div>
    <div><Label>Recurring</Label><div style={{ fontSize: 14, color: CRC.textGrey }}>{money(e.recurring)}</div></div>
    <div><Label>Total</Label><div style={{ fontSize: 16, fontWeight: 700, color: CRC.blueprintBlue }}>{money(e.total)}</div></div>
  </div>
);

export const VariantMasterDetail: React.FC<Props> = ({ project, setProject }) => {
  const [selectedKey, setSelectedKey] = useState<ModuleKey>(project.modules[0].key);
  const months = project.durationMonths;
  const selected = project.modules.find((m) => m.key === selectedKey) ?? project.modules[0];
  const projEst = estimateProject(project);

  const patchModule = (key: ModuleKey, patch: Partial<ModuleState>) =>
    setProject((p) => ({ ...p, modules: p.modules.map((m) => (m.key === key ? { ...m, ...patch } : m)) }));
  const patchSource = (key: ModuleKey, id: string, patch: Partial<SourceRow>) =>
    setProject((p) => ({ ...p, modules: p.modules.map((m) => (m.key === key ? { ...m, sources: m.sources.map((s) => (s.id === id ? { ...s, ...patch } : s)) } : m)) }));
  const addSource = (m: ModuleState) => {
    const row: SourceRow = { id: `${m.key}-${Date.now()}`, name: '', difficulty: 'spreadsheet', dataTypes: [] };
    if (m.hasCapturePath) { row.capturePath = 'C'; row.serviceDays = 0; row.boltonMachines = 0; }
    patchModule(m.key, { sources: [...m.sources, row] });
  };
  const removeSource = (m: ModuleState, id: string) => patchModule(m.key, { sources: m.sources.filter((s) => s.id !== id) });
  const toggleDataType = (m: ModuleState, s: SourceRow, t: string) =>
    patchSource(m.key, s.id, { dataTypes: s.dataTypes.includes(t) ? s.dataTypes.filter((x) => x !== t) : [...s.dataTypes, t] });

  return (
    <div style={{ display: 'flex', width: '100%', minHeight: '100%', fontFamily: 'inherit', color: CRC.textGrey }}>
      {/* LEFT RAIL */}
      <div style={{ width: 300, minWidth: 300, background: '#fff', borderRight: `1px solid ${CRC.border}`, display: 'flex', flexDirection: 'column', padding: 18 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 18, color: CRC.blueprintBlue }}>Pricing inputs</h2>
        <div style={{ marginBottom: 18 }}>
          <Label>Duration</Label>
          <div style={{ marginTop: 4 }}>
            <NumberInput value={months} onChange={(v) => setProject((p) => ({ ...p, durationMonths: v }))} width={90} suffix="mo" />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {project.modules.map((m) => {
            const isSel = m.key === selectedKey;
            const e = estimateModule(m, months);
            return (
              <div key={m.key} onClick={() => setSelectedKey(m.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 10px 12px', borderRadius: 8, cursor: 'pointer', position: 'relative', background: isSel ? CRC.heroRed10 : 'transparent' }}>
                {isSel && <span style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 4, borderRadius: 4, background: CRC.heroRed }} />}
                <span onClick={(ev) => { ev.stopPropagation(); patchModule(m.key, { enabled: !m.enabled }); }}>
                  <Toggle on={m.enabled} onClick={() => {}} />
                </span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: m.enabled ? CRC.blueprintBlue : CRC.heritageGrey }}>{m.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: isSel ? CRC.readableRed : CRC.heritageGrey, background: isSel ? '#fff' : CRC.pageBg, border: `1px solid ${CRC.border}`, borderRadius: 999, padding: '2px 8px' }}>{money(e.total)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${CRC.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}><span style={{ color: CRC.heritageGrey }}>One-time</span><span>{money(projEst.oneTime)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}><span style={{ color: CRC.heritageGrey }}>Recurring</span><span>{money(projEst.recurring)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: CRC.blueprintBlue }}><span>Total</span><span>{money(projEst.total)}</span></div>
        </div>
      </div>

      {/* RIGHT PANE */}
      <div style={{ flex: 1, padding: 28, background: CRC.pageBg, overflow: 'auto' }}>
        {!selected.enabled ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CRC.heritageGrey, fontSize: 14 }}>
            Enable this module to configure pricing
          </div>
        ) : (
          <ModuleDetail
            m={selected} months={months}
            patchModule={patchModule} patchSource={patchSource}
            addSource={addSource} removeSource={removeSource} toggleDataType={toggleDataType}
          />
        )}
      </div>
    </div>
  );
};

interface DetailProps {
  m: ModuleState; months: number;
  patchModule: (key: ModuleKey, patch: Partial<ModuleState>) => void;
  patchSource: (key: ModuleKey, id: string, patch: Partial<SourceRow>) => void;
  addSource: (m: ModuleState) => void;
  removeSource: (m: ModuleState, id: string) => void;
  toggleDataType: (m: ModuleState, s: SourceRow, t: string) => void;
}

const ModuleDetail: React.FC<DetailProps> = ({ m, months, patchModule, patchSource, addSource, removeSource, toggleDataType }) => {
  const [openTypes, setOpenTypes] = useState<string | null>(null);
  const e = estimateModule(m, months);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1100 }}>
      {/* Header card */}
      <Card style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: CRC.blueprintBlue }}>{m.name}</div>
            <div style={{ fontSize: 12, color: CRC.heritageGrey, marginBottom: 12 }}>{m.rowNoun} onboarding & volume</div>
            <Label>{m.volumeLabel}</Label>
            <div style={{ marginTop: 4 }}>
              <NumberInput value={m.volume} onChange={(v) => patchModule(m.key, { volume: v })} width={140} />
            </div>
          </div>
          <EstStat e={e} />
        </div>
      </Card>

      {/* Data sources */}
      <Card style={{ padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: CRC.blueprintBlue, marginBottom: 12 }}>Data sources</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: CRC.pageBg }}>
              <th style={th}>{m.rowNoun}</th>
              <th style={th}>Source format</th>
              <th style={th}>Data types</th>
              <th style={th}>NRE override</th>
              {m.hasCapturePath && <th style={th}>Capture path</th>}
              {m.hasCapturePath && <th style={th}>Days / Machines</th>}
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {m.sources.map((s) => (
              <tr key={s.id}>
                <td style={td}><TextInput value={s.name} onChange={(v) => patchSource(m.key, s.id, { name: v })} width={150} placeholder={m.rowNoun} /></td>
                <td style={td}>
                  <Select value={s.difficulty} onChange={(v) => patchSource(m.key, s.id, { difficulty: v as SourceRow['difficulty'] })}
                    options={DIFFICULTY.map((d) => ({ value: d.key, label: d.label }))} width={150} />
                </td>
                <td style={{ ...td, position: 'relative' }}>
                  <button onClick={() => setOpenTypes(openTypes === s.id ? null : s.id)}
                    style={{ padding: '7px 10px', border: `1px solid ${CRC.border}`, borderRadius: 8, fontSize: 12, color: CRC.textGrey, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {s.dataTypes.length} selected
                  </button>
                  {openTypes === s.id && (
                    <div style={{ position: 'absolute', zIndex: 5, marginTop: 6, padding: 10, background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 10, boxShadow: '0 4px 14px rgba(0,0,0,0.1)', display: 'flex', flexWrap: 'wrap', gap: 6, width: 240 }}>
                      {DATA_TYPES[m.key].map((t) => (
                        <Chip key={t} active={s.dataTypes.includes(t)} onClick={() => toggleDataType(m, s, t)}>{t}</Chip>
                      ))}
                    </div>
                  )}
                </td>
                <td style={td}>
                  <NumberInput value={s.nreOverride ?? 0} onChange={(v) => patchSource(m.key, s.id, { nreOverride: v || undefined })} width={90} />
                </td>
                {m.hasCapturePath && (
                  <td style={td}>
                    <Select value={s.capturePath ?? 'C'} onChange={(v) => patchSource(m.key, s.id, { capturePath: v as SourceRow['capturePath'] })}
                      options={(['A', 'B', 'C'] as const).map((p) => ({ value: p, label: CAPTURE_PATH_LABELS[p] }))} width={170} />
                  </td>
                )}
                {m.hasCapturePath && (
                  <td style={td}>
                    {s.capturePath === 'A' && <NumberInput value={s.serviceDays ?? 0} onChange={(v) => patchSource(m.key, s.id, { serviceDays: v })} width={90} suffix="days" />}
                    {s.capturePath === 'B' && <NumberInput value={s.boltonMachines ?? 0} onChange={(v) => patchSource(m.key, s.id, { boltonMachines: v })} width={90} suffix="mach" />}
                    {s.capturePath === 'C' && <span style={{ color: CRC.heritageGrey }}>—</span>}
                  </td>
                )}
                <td style={td}>
                  <button onClick={() => removeSource(m, s.id)} style={{ border: 'none', background: 'none', color: CRC.readableRed, cursor: 'pointer', fontSize: 16, fontFamily: 'inherit' }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={() => addSource(m)}
          style={{ marginTop: 14, padding: '8px 14px', border: 'none', borderRadius: 8, background: CRC.heroRed, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add {m.rowNoun}
        </button>
      </Card>

      {/* Module options */}
      <Card style={{ padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: CRC.blueprintBlue, marginBottom: 14 }}>Module options</div>
        {m.key === 'materialTraceability' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Toggle on={!!m.mtAddons?.gas} onClick={() => patchModule(m.key, { mtAddons: { ...m.mtAddons!, gas: !m.mtAddons?.gas } })} label="Gas certs" />
            <Toggle on={!!m.mtAddons?.wire} onClick={() => patchModule(m.key, { mtAddons: { ...m.mtAddons!, wire: !m.mtAddons?.wire } })} label="Welding wire certs" />
            <Toggle on={!!m.mtAddons?.coatingMaterials} onClick={() => patchModule(m.key, { mtAddons: { ...m.mtAddons!, coatingMaterials: !m.mtAddons?.coatingMaterials } })} label="Coating materials" />
            <div><Label>CRCE pipe-end scan</Label><div style={{ marginTop: 4 }}><NumberInput value={m.mtPipeEndScanDays ?? 0} onChange={(v) => patchModule(m.key, { mtPipeEndScanDays: v })} width={100} suffix="days" /></div></div>
          </div>
        )}
        {m.key === 'pipeFitter' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><Label>Scanning mode</Label><div style={{ marginTop: 6 }}>
              <Segmented value={m.pfScanningMode ?? 'none'} onChange={(v) => patchModule(m.key, { pfScanningMode: v as ModuleState['pfScanningMode'] })}
                options={[{ value: 'none', label: 'None' }, { value: 'byod', label: 'BYOD' }, { value: 'crew', label: 'CRCE crew' }]} />
            </div></div>
            {m.pfScanningMode === 'crew' && (
              <div><Label>Crew days</Label><div style={{ marginTop: 4 }}><NumberInput value={m.pfScanningCrewDays ?? 0} onChange={(v) => patchModule(m.key, { pfScanningCrewDays: v })} width={100} suffix="days" /></div></div>
            )}
            <Toggle on={!!m.pfReporting} onClick={() => patchModule(m.key, { pfReporting: !m.pfReporting })} label="Reporting" />
            <Toggle on={!!m.pfAlgorithm} onClick={() => patchModule(m.key, { pfAlgorithm: !m.pfAlgorithm })} label="Algorithm (OmniFit/OptiFit)" />
          </div>
        )}
        {m.key === 'ndt' && (
          <div style={{ fontSize: 13, color: CRC.textGrey }}>Per-inspection rate includes imagery storage. No CRCE hardware (vendor owns scanners).</div>
        )}
        {m.key === 'coating' && (
          <div style={{ fontSize: 13, color: CRC.textGrey }}>Capture path is set per contractor row above.</div>
        )}
      </Card>
    </div>
  );
};
